import { parseISO } from 'date-fns';
import { MapElement } from './map/map-element.model';
import { Point, Geometry } from 'geojson';
import { MapObject } from './map/types';

// https://stackoverflow.com/a/51365037
export type RecursivePartial<T> = {
  [P in keyof T]?:
    T[P] extends (infer U)[] ? RecursivePartial<U>[] :
    T[P] extends object ? RecursivePartial<T[P]> :
    T[P];
};

/**
 * At compile time, verifies that `A` extends `B`.
 */
export type AssertExtends<A extends B, B> = A;

/**
 * Helper function for translations. Can be used to mark strings for translation in Typescript code
 * @param {string} s
 * @returns {string}
 * @private
 */
export function _(s: string) {
  return s;
}

/**
 * Helper function for Mapbox, returns true if browser is Firefox
 * @returns {boolean}
 */
export function fixFirefoxPrint() {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
}

export function arraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = arr1.length; i--;) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

export function getDataName(tenantId: number) {
  switch (tenantId) {
    case 2:
      return '';
    case 3:
      return 'finavia';
    default:
      return 'miproData';
  }
}

export function strEnum<T extends string>(o: Array<T>): { [K in T]: K } {
  return o.reduce((res, key) => {
    res[key] = key;
    return res;
  }, Object.create(null));
}

/**
 * Get color code for color string
 * @param {string} key - for example, "success-base"
 * @returns {string}
 */
export function getCSSColorVariableValue(key: string): string {
  return window.getComputedStyle(document.body).getPropertyValue('--color-' + key);
}

export function parseToDate(value: string | Date | number): Date {
  if (typeof value === 'number') { // Timestamp
    return new Date(value);
  } else if (typeof value === 'object') { // Date object
    return value;
  } else { // Probably string
    const v = parseISO(value) as any;
    if (v === 'Invalid Date') {
      return new Date(parseInt(value, 10));
    }
    return v;
  }
}

export function convertFeatureToMapObject(feature: MapElement<Geometry, Point>, layerName?: string): MapObject {
  return {
    id: feature.properties[feature.layer.metadata.uniqueBy] + '',
    layerId: feature.layer.id,
    title: layerName ?
      `${layerName}: ${feature.properties[feature.layer.metadata.labelBy]}` :
      feature.properties[feature.layer.metadata.labelBy]
  };
}

/**
 * Prevents the big red disaster recovery button ("clear cache and log out") from appearing.
 * This should be called when the application has succesfully initialized.
 */
export function preventDisasterButton(): void {
  if (globalThis.fatalButtonTimeout !== undefined) {
    console.log('Clearing fatalButtonTimeout');
    clearTimeout(globalThis.fatalButtonTimeout);
    globalThis.fatalButtonTimeout = undefined;
  }

  document.getElementById('disasterButton').style.display = 'none';
}

export function cacheBust(url: string) {
  return url + '?cache-bust=' + Math.random();
}

/**
 * Groups entries using a projected key, and an optionally projected value.
 *
 * @example
 * // returns Map([ [3, ['foo', 'bar']], [4, ['test']] ])
 * groupBy(['foo', 'test', 'bar'], x => x.length)
 *
 * @example
 * // returns Map([ [3, ['FOO', 'BAR']], [4, ['TEST']] ])
 * groupBy(['foo', 'test', 'bar'], x => x.length, x => x.toUpperCase())
 *
 * @example <caption>Motivation behind valueProjector</caption>
 * // returns Map([ ['key1', ['foo', 'bar']], ['key2', ['test']] ]),
 * // removing the extra object wrapping every value in the process
 * groupBy([
 *   {key: 'key1', 'value': 'foo'},
 *   {key: 'key2', 'value': 'test'},
 *   {key: 'key1', 'value': 'bar'},
 * ], x => x.key, x => x.value)
 */
export function groupBy<K, V>(
  collection: Iterable<V>,
  keyProjector: (entry: V) => K,
): Map<K, V[]>;
export function groupBy<K, V, T>(
  collection: Iterable<T>,
  keyProjector: (entry: T) => K,
  valueProjector: (entry: T) => V,
): Map<K, V[]>;
export function groupBy<K, V, T>(
  collection: Iterable<T>,
  keyProjector: (entry: T) => K,
  // the overload signatures ensure this cast is valid
  valueProjector: (entry: T) => V = (x) => x as unknown as V,
): Map<K, V[]> {
  const output = new Map<K, V[]>();
  for (const rawValue of collection) {
    const key = keyProjector(rawValue);
    const value = valueProjector(rawValue);
    const arr = output.get(key);
    if (arr === undefined) {
      output.set(key, [value]);
    } else {
      arr.push(value);
    }
  }
  return output;
}

/**
 * @returns A Promise that resolves after the given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * Like a builtin `Map`, except when an item that doesn't exist is `get`,
 * the callback given to the constructor is used to create and insert one.
 * @example
 *    const map = new DefaultMap(() => Math.random());
 *    map.set('foo', 10);
 *    map.get('foo'); // 10
 *    map.get('bar'); // random number
 *    map.get('bar'); // same random number
 *    map.get('baz'); // different random number
 */
export class DefaultMap<K, V> extends Map<K, V> {
  constructor(
    private readonly valueCreator: (key: K) => V,
  ) {
    super();
  }

  public get(key: K): V {
    if (this.has(key)) {
      return super.get(key);
    }
    const value = this.valueCreator(key);
    this.set(key, value);
    return value;
  }
}
